import type {
  CSSProperties,
  Dispatch,
  MouseEventHandler,
  SetStateAction,
} from 'react'
import { memo, useMemo, useRef, useState } from 'react'

import { format } from 'date-fns/format'
import { isAfter } from 'date-fns/isAfter'
import { subMonths } from 'date-fns/subMonths'
import { useUnit } from 'effector-react'
import { first } from 'lodash'

import { Tooltip } from '@/components/ui/tooltip'
import { $fanId, useChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { $typingFanIds } from '@/models/chattingUI/chats/typing.ts'
import { useCountdowns } from '@/modules/chatting/state/countdown-provider.tsx'
import type { Chat } from '@/modules/chatting/types.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import type { ColorVariant } from '@/modules/creator-settings/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { darkenHexColorWithAlpha } from '@/utils/colors'
import { classNames } from '@/utils/helpers'

import SubscriptionTagIcon from '../../../icons/subscription-tag.svg'
import { useChatting } from '../../../state/chatting-provider'
import { formatNumber, getSubscriptionTagConfig } from '../../utils'
import { ChatInfo } from '../chat-info'

export interface FloatingChatInfoConfig {
  position: Record<'top' | 'left', number>
  chat: Chat
  visible: boolean
}

export interface ChatItemProps {
  chatId: number
  isSidebarExpanded: boolean
  onShowFloatingInfo: Dispatch<SetStateAction<FloatingChatInfoConfig | null>>
  onClick: () => void
}

const ChatItemComponent = ({
  chatId,
  isSidebarExpanded,
  onShowFloatingInfo,
  onClick,
}: ChatItemProps) => {
  const fanId = useUnit($fanId)
  const typingFanIds = useUnit($typingFanIds)
  const { highlightedNumber } = useChatting()
  const { countdowns } = useCountdowns()
  const { colors, defaultColor, showFanSpending } = useSettingsContext()
  const [isHovered, setIsHovered] = useState(false)
  const chat = useChatDetails(chatId)!
  const {
    withUser: {
      avatar,
      displayName,
      online,
      id,
      name,
      subscribedOnData,
      fanNumber,
    },
  } = chat
  const isFanHighlighted = fanNumber === highlightedNumber

  const isSubscriptionNew =
    subscribedOnData?.subscribeAt &&
    isAfter(new Date(subscribedOnData.subscribeAt), subMonths(new Date(), 1))
  const subscriptionStart =
    subscribedOnData?.subscribeAt &&
    format(new Date(subscribedOnData.subscribeAt), 'MMM dd, yyyy')
  const subscriptionTagConfig = getSubscriptionTagConfig(chat.withUser)
  const ref = useRef<HTMLDivElement | null>(null)

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = () => {
    setIsHovered(true)

    if (ref && !isSidebarExpanded) {
      const rect = ref.current?.getBoundingClientRect()

      if (rect) {
        const top = rect.top + rect.height / 2
        const left = rect.right

        onShowFloatingInfo({ position: { top, left }, chat, visible: true })
      }
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    onShowFloatingInfo((prev) => (prev ? { ...prev, visible: false } : null))
  }

  const spend = subscribedOnData
    ? (subscribedOnData?.totalSumm || 0) -
      (subscribedOnData?.subscribesSumm || 0)
    : 0

  const fanChatDisplaySettings: ColorVariant | null = useMemo(() => {
    const colorsSettings = {
      0: defaultColor!,
      ...colors,
    }

    return (
      (first(
        Object.entries(colorsSettings)
          .filter(([limit]) => spend >= Number(limit))
          .sort(([a], [b]) => Number(b) - Number(a)),
      )?.[1] as ColorVariant) || null
    )
  }, [colors, defaultColor, spend])

  const colorStyle: CSSProperties = {}

  const isTyping = !!id && typingFanIds.includes(id)

  if (fanId !== chatId && fanChatDisplaySettings?.backgroundColorHex) {
    colorStyle.backgroundColor = !isHovered
      ? fanChatDisplaySettings?.backgroundColorHex
      : darkenHexColorWithAlpha(fanChatDisplaySettings?.backgroundColorHex, 20)
  }

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classNames(
        'hover:bg-primary-50 dark:hover:bg-primary-900 w-full cursor-pointer border-transparent p-[14px] box-border',
        isSidebarExpanded && 'rounded-md',
        isFanHighlighted && 'border-primary-500 border-2',
        !isSidebarExpanded && 'border-transparent border-l-primary-500',
        fanId === chatId &&
          'bg-primary-50 dark:bg-secondary-900 border-r-transparent',
      )}
      style={colorStyle}
      onClick={onClick}
    >
      <div className="flex gap-3">
        <div className="relative h-[50px] w-[50px] rounded-full">
          {isSubscriptionNew && (
            <Tooltip
              content={`New Subscriber - since ${subscriptionStart}`}
              className="absolute -left-1 -top-[10px] z-10"
            >
              <div className="dark:border-secondary-700 rounded-full border border-white bg-teal-600 px-1 text-xs font-medium leading-[18px] text-white">
                New
              </div>
            </Tooltip>
          )}
          {subscriptionTagConfig && (
            <Tooltip
              content={subscriptionTagConfig.tooltip}
              className="absolute -left-[7px] bottom-0 z-10"
            >
              <SubscriptionTagIcon
                className={subscriptionTagConfig.bgClassName}
              />
            </Tooltip>
          )}
          {online && (
            <div className="bg-success-default dark:border-secondary-700 absolute top-0 right-1 z-10 h-2.5 w-2.5 rounded-full border border-white" />
          )}
          {showFanSpending && (
            <div className="absolute inset-0 z-[1]">
              <div
                className={classNames(
                  'absolute -bottom-1 left-0 right-0 text-center text-[10px] leading-[18px] text-white rounded-md',
                )}
                style={{
                  backgroundColor: fanChatDisplaySettings?.colorHex,
                }}
              >
                ${formatNumber(Math.trunc(spend))}
              </div>
            </div>
          )}
          <CreatorAvatar
            size={50}
            avatarSrc={
              chat.withUser.avatarThumbs?.c50 ??
              chat.withUser.avatarThumbs?.c144 ??
              avatar
            }
            name={displayName ?? name}
          />
        </div>
        {isSidebarExpanded && (
          <ChatInfo
            chat={chat}
            countdown={countdowns[id]}
            isTyping={isTyping}
          />
        )}
      </div>
    </div>
  )
}

export const ChatItem = memo(ChatItemComponent)
